import { useState, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Info, AlertTriangle } from "lucide-react";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useConnectStore } from "@/dentlab/src/store/Connect";
import { showNotification } from "@/dentlab/src/store/Central/selectors";
import { getOrCreateVendorOrg } from "@/dentconnect/src/pages/job-request/utils/job-request.utils";
import { sendVendorInviteEmail } from "@/lib/email";
import { supabase } from "@/lib/supabase";
import posthog from "posthog-js";

interface AddVendorDialogProps {
	open: boolean;
	onClose: () => void;
	organization: { id: string; name: string };
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const AddVendorDialog: React.FC<AddVendorDialogProps> = ({
	open,
	onClose,
	organization,
}) => {
	const [email, setEmail] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [backendCheck, setBackendCheck] = useState<{
		exists: boolean;
		organizationId?: string;
		organizationName?: string;
	} | null>(null);
	const [isChecking, setIsChecking] = useState(false);
	const checkTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

	const navigate = useNavigate();

	const connectedVendors = useConnectStore((state) => state.connectedVendors);
	const brandsByOrgId = useConnectStore((state) => state.brandsByOrgId);
	const fetchConnectedVendors = useConnectStore(
		(state) => state.fetchConnectedVendors
	);
	const fetchBrands = useConnectStore((state) => state.fetchBrands);

	const trimmedEmail = email.trim();
	const isValidEmail = EMAIL_REGEX.test(trimmedEmail);

	const alreadyConnectedVendor = useMemo(() => {
		if (!isValidEmail) return null;
		const normalized = trimmedEmail.toLowerCase();
		// Local check via admin_email (instant)
		for (const [orgId, brand] of Object.entries(brandsByOrgId)) {
			if (
				brand.admin_email?.toLowerCase() === normalized &&
				connectedVendors.includes(orgId)
			) {
				return brand.name;
			}
		}
		// Backend check via organizationId (after debounced query)
		if (
			backendCheck?.exists &&
			backendCheck.organizationId &&
			connectedVendors.includes(backendCheck.organizationId)
		) {
			return (
				brandsByOrgId[backendCheck.organizationId]?.name ??
				backendCheck.organizationName ??
				"Unbekannt"
			);
		}
		return null;
	}, [
		trimmedEmail,
		isValidEmail,
		brandsByOrgId,
		connectedVendors,
		backendCheck,
	]);

	const handleEmailChange = useCallback((value: string) => {
		setEmail(value);
		setBackendCheck(null);

		if (checkTimeoutRef.current) {
			clearTimeout(checkTimeoutRef.current);
		}

		const normalized = value.trim();
		if (!EMAIL_REGEX.test(normalized)) {
			setIsChecking(false);
			return;
		}

		setIsChecking(true);
		checkTimeoutRef.current = setTimeout(async () => {
			try {
				const { data: profiles } = await supabase
					.from("profiles")
					.select("id, organization_id")
					.eq("email", normalized.toLowerCase())
					.limit(1);
				const profile = profiles?.[0];
				if (!profile) {
					setBackendCheck({ exists: false });
					return;
				}
				const { data: orgs } = await supabase
					.from("organizations")
					.select("id, name")
					.eq("id", profile.organization_id)
					.limit(1);
				const org = orgs?.[0];
				setBackendCheck({
					exists: true,
					organizationId: profile.organization_id,
					organizationName: org?.name ?? undefined,
				});
			} catch {
				// non-critical hint check
			} finally {
				setIsChecking(false);
			}
		}, 500);
	}, []);

	const handleClose = useCallback(() => {
		setEmail("");
		setBackendCheck(null);
		setIsChecking(false);
		if (checkTimeoutRef.current) {
			clearTimeout(checkTimeoutRef.current);
		}
		onClose();
	}, [onClose]);

	const handleSubmit = useCallback(async () => {
		if (!isValidEmail || alreadyConnectedVendor || !organization?.id)
			return;

		setIsSubmitting(true);
		try {
			const vendorOrgId = await getOrCreateVendorOrg(trimmedEmail);
			if (!vendorOrgId) return;

			await supabase.rpc("get_or_create_general_channel", {
				p_vendor_org_id: vendorOrgId,
				p_customer_org_id: organization.id,
			});

			if (organization.name) {
				sendVendorInviteEmail({
					recipientEmail: trimmedEmail,
					labName: organization.name,
				});
			}

			posthog.capture("vendor_invite_sent", {
				recipient_email: trimmedEmail,
				recipient_already_on_platform: !!backendCheck?.exists,
				source: "add_vendor_dialog",
			});
			posthog.group("organization", organization.id, {
				$set_once: { first_invite_sent_at: new Date().toISOString() },
			});

			await Promise.all([
				fetchConnectedVendors(organization.id),
				fetchBrands(organization.id),
			]);

			handleClose();
			navigate(`/vendor/${vendorOrgId}`);
			showNotification({
				message: "Fremdlabor hinzugefügt",
				type: "success",
			});
		} catch {
			showNotification({
				message: "Fehler beim Hinzufügen des Fremdlabors",
				type: "error",
			});
		} finally {
			setIsSubmitting(false);
		}
	}, [
		isValidEmail,
		alreadyConnectedVendor,
		organization,
		trimmedEmail,
		fetchConnectedVendors,
		fetchBrands,
		handleClose,
		navigate,
	]);

	const canSubmit = isValidEmail && !alreadyConnectedVendor && !isSubmitting;

	return (
		<Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
			<DialogContent
				className="sm:max-w-md"
				aria-label="Neues Fremdlabor hinzufügen"
				data-testid="add-vendor-dialog"
			>
				<DialogHeader>
					<DialogTitle>Neues Fremdlabor hinzufügen</DialogTitle>
					<DialogDescription>
						Geben Sie die E-Mail-Adresse des Labors ein, um es als
						Fremdlabor hinzuzufügen.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-3 py-2">
					<div className="space-y-2">
						<Label htmlFor="vendor-email">
							E-Mail-Adresse des Labors
						</Label>
						<Input
							id="vendor-email"
							type="email"
							placeholder="labor@beispiel.ch"
							value={email}
							onChange={(e) => handleEmailChange(e.target.value)}
							disabled={isSubmitting}
							aria-label="E-Mail-Adresse des Fremdlabors"
							data-testid="add-vendor-email-input"
							onKeyDown={(e) => {
								if (e.key === "Enter" && canSubmit) {
									handleSubmit();
								}
							}}
						/>
					</div>

					{isValidEmail && alreadyConnectedVendor && (
						<div
							className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 rounded-md px-3 py-2"
							data-testid="add-vendor-email-hint"
						>
							<AlertTriangle className="h-4 w-4 flex-shrink-0" />
							<span>
								Bereits als Fremdlabor verknüpft
								{alreadyConnectedVendor !== "Unbekannt" &&
									` (${alreadyConnectedVendor})`}
							</span>
						</div>
					)}

					{isValidEmail && !alreadyConnectedVendor && isChecking && (
						<div className="flex items-center gap-2 text-sm text-muted-foreground px-3 py-2">
							<Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
							<span>Prüfe E-Mail...</span>
						</div>
					)}

					{isValidEmail &&
						!alreadyConnectedVendor &&
						!isChecking &&
						backendCheck?.exists && (
							<div
								className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 rounded-md px-3 py-2"
								data-testid="add-vendor-email-hint"
							>
								<Info className="h-4 w-4 flex-shrink-0" />
								<span>
									Bereits auf dentDESK
									{backendCheck.organizationName &&
										` (${backendCheck.organizationName})`}
								</span>
							</div>
						)}
				</div>

				<DialogFooter className="flex-row gap-2 sm:justify-end">
					<Button
						variant="outline"
						onClick={handleClose}
						disabled={isSubmitting}
						aria-label="Abbrechen"
						data-testid="add-vendor-cancel-button"
					>
						Abbrechen
					</Button>
					<Button
						onClick={handleSubmit}
						disabled={!canSubmit}
						aria-label="Fremdlabor hinzufügen"
						data-testid="add-vendor-submit-button"
					>
						{isSubmitting ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								Hinzufügen...
							</>
						) : (
							"Hinzufügen"
						)}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
